import { track } from '@/lib/analytics'

// Chrome-only and not in lib.dom.d.ts.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** How this browser installs the app: `prompt` has a real Install dialog (Chrome/Android),
 *  `ios` only via Share → Add to Home Screen, `null` can't (or already has). */
export type InstallMethod = 'prompt' | 'ios' | null

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((l) => l())
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function isIos(): boolean {
  // iPadOS reports itself as a Mac, so tell it apart by touch support.
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/** Chrome fires `beforeinstallprompt` once, early — often before React mounts — so this is
 *  called from main.tsx to catch it, not from a component. */
export function listenForInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stops Chrome's own mini-infobar; we show the install offer ourselves.
    e.preventDefault()
    deferred = e as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    installed = true
    deferred = null
    track('app_installed')
    notify()
  })
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getInstallMethod(): InstallMethod {
  if (installed || isStandalone()) return null
  if (deferred) return 'prompt'
  return isIos() ? 'ios' : null
}

/** Opens Chrome's install dialog. The event can only be used once, so it's dropped after. */
export async function promptInstall(): Promise<void> {
  const event = deferred
  if (!event) return
  deferred = null
  notify()
  await event.prompt()
}
